import type { RosterUploadSummary } from '../../../types/adminRosterUploads';
import { formatCount, formatDuration, formatInstant, formatRelative } from './rosterUploadFormat';

/**
 * Whether the figures describe the current filters.
 *
 * - `loading`: a newer response is on its way.
 * - `outdated`: the latest request failed, so these are from an earlier, successful one.
 */
export type SummaryFreshness = 'current' | 'loading' | 'outdated';

interface RosterUploadsSummaryProps {
  summary: RosterUploadSummary;
  now: Date;
  freshness: SummaryFreshness;
}

const FRESHNESS_NOTE: Record<Exclude<SummaryFreshness, 'current'>, string> = {
  loading: 'Updating figures for these filters...',
  outdated: 'These figures are from the last successful load and may not match these filters.',
};

interface Tile {
  label: string;
  value: string;
  detail?: string;
}

/**
 * Stat tiles over everything the current filters match — not just the visible page, which is why
 * they come from the API's summary rather than being counted from the rows.
 */
export const RosterUploadsSummary = ({ summary, now, freshness }: RosterUploadsSummaryProps) => {
  const stale = freshness !== 'current';
  const tiles: Tile[] = [
    { label: 'Total uploads', value: formatCount(summary.totalUploads) },
    { label: 'Last 7 days', value: formatCount(summary.uploadsLast7Days) },
    { label: 'Last 30 days', value: formatCount(summary.uploadsLast30Days) },
    { label: 'Completed', value: formatCount(summary.completedCount) },
    { label: 'Failed', value: formatCount(summary.failedCount) },
    { label: 'In progress', value: formatCount(summary.inProgressCount) },
    { label: 'Organizations', value: formatCount(summary.distinctOrganizations) },
    { label: 'Uploaders', value: formatCount(summary.distinctUploaders) },
    {
      label: 'Avg processing time',
      value: formatDuration(summary.avgProcessingTimeMs),
      detail: 'Completed uploads',
    },
    { label: 'Total tokens', value: formatCount(summary.totalTokens) },
    {
      label: 'Last upload',
      value: formatRelative(summary.lastUploadAt, now),
      detail: summary.lastUploadAt ? formatInstant(summary.lastUploadAt) : undefined,
    },
  ];

  /*
    Staleness is said in words and marked with a dashed tile border, never by fading: the old 0.6
    opacity took the grey captions down to about 3:1.
  */
  return (
    <>
      {freshness !== 'current' && (
        <p className="roster-uploads__summary-note" data-testid="roster-uploads-summary-note">
          {FRESHNESS_NOTE[freshness]}
        </p>
      )}
    <dl
      className={`roster-uploads__summary${stale ? ' roster-uploads__summary--stale' : ''}`}
      aria-busy={freshness === 'loading' || undefined}
      data-testid="roster-uploads-summary"
    >
      {tiles.map((tile) => (
        <div className="roster-uploads__tile" key={tile.label}>
          <dt className="roster-uploads__tile-label">{tile.label}</dt>
          <dd className="roster-uploads__tile-value">{tile.value}</dd>
          {tile.detail && <dd className="roster-uploads__tile-detail">{tile.detail}</dd>}
        </div>
      ))}
    </dl>
    </>
  );
};
